import * as THREE from 'three';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/helpers/auth.service';
import { GeneralCursors, ObstacleCursors, PanelArrayCursors, PanelMoveCursors, RoofCursors, RulerCursors, TreeCursors } from './helpers/cursors/cursorHelper';
import { RoofSection } from '@design/models/roof-section.model';
import { strFromU8, decompressSync } from 'fflate';
import axios from 'axios';
import { Panel } from '@design/models/panel.model';

// Toast notification types for panel move/select features
export interface ToastNotification {
  message: string;
  type: 'success' | 'warning' | 'danger' | 'info';
  timeOut?: number;
}

export const parseToJson = (bin: any) => {
  return strFromU8(decompressSync(new Uint8Array(bin)));
}
/* gzip json ::  json */
export const getHeightJSON = async (url: string) => {
  let inflatedJSON = {};
  try {
    const { data } = await axios.get(url, { responseType: 'arraybuffer', decompress: true });
    // parse json
    inflatedJSON = JSON.parse(parseToJson(data))
  } catch (error) {
    console.error('could not fetch gzip json', error);
  }
  return inflatedJSON;
};

@Injectable({ providedIn: 'root' })
export class EngineService implements OnDestroy {
  private canvas: HTMLCanvasElement | any;
  private camera: THREE.PerspectiveCamera | any;
  private light: THREE.AmbientLight | any;

  private cube: THREE.Mesh | any;
  public lowQualityTexture!: THREE.Texture;
  private frameId: number | any = null;
  public scene!: THREE.Scene;
  public renderer: THREE.WebGLRenderer | any;
  public constructor(private authService :AuthService ) {}
  $mouseDown: Subject<boolean> = new Subject<boolean>();
  updateMouseDownState(event: boolean){
    this.$mouseDown.next(event);
  }

  isMouseDown(){
    return this.$mouseDown.asObservable();
  }

  public draggableObjects: Array<THREE.Mesh> = []; // Maintain a local copy
  updateDraggableObjects(newObjects: Array<THREE.Mesh>) {
    newObjects.forEach(newObj => {
      // Remove any existing object with the same ID
      const index = this.draggableObjects.findIndex(obj => obj.name === newObj.name);
      if (index !== -1) {
        this.draggableObjects.splice(index, 1); // Remove the existing duplicate
      }
      this.draggableObjects.push(newObj);
    });
  }

  removeDraggableObject(uuid: any) {
    const index = this.draggableObjects.findIndex(obj => (obj.uuid === uuid || obj.name === uuid));
    if (index !== -1) {
      this.draggableObjects.splice(index, 1);
    }
  }

  $camType: BehaviorSubject<string> = new BehaviorSubject<string>('2D');
  updateCamType(event: string){
    this.$camType.next(event);
  }

  getCamType(){
    return this.$camType.asObservable();
  }

  $focusOutOfDesign: Subject<boolean> = new Subject<boolean>;
  updateFocusOut(value: boolean){
    this.$focusOutOfDesign.next(value);
  }

  isFocusOut(){
    return this.$focusOutOfDesign.asObservable();
  }

  $roofSelected: Subject<string> = new Subject<string>();
  updateRoofSelectedState(event: string){
    this.$roofSelected.next(event);
  }

  getRoofSelectedState(){
    return this.$roofSelected.asObservable();
  }

  $treeCompleted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  updateTreeStatus(event: boolean){
    this.$treeCompleted.next(event);
  }

  getTreeStatus(){
    return this.$treeCompleted.asObservable();
  }

  $treeMoveStarted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  updateTreeMove(event: boolean){
    this.$treeMoveStarted.next(event);
  }

  getTreeMove(){
    return this.$treeMoveStarted.asObservable();
  }

  $toggleOrbit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  toggleOrbit(event: boolean){
    this.$toggleOrbit.next(event);
  }

  getOrbitValue(){
    return this.$toggleOrbit.asObservable();
  }

  $treeCursorState: Subject<TreeCursors> = new Subject<TreeCursors>();
  updateTreeCursorState(event: TreeCursors){
    this.$treeCursorState.next(event);
  }

  getTreeCursorState(){
    return this.$treeCursorState.asObservable();
  }

  $obstacleCursorState: Subject<ObstacleCursors> = new Subject<ObstacleCursors>();
  updateObstacleCursorState(event: ObstacleCursors){
    this.$obstacleCursorState.next(event);
  }

  getObstacleCursorState(){
    return this.$obstacleCursorState.asObservable();
  }

  $generalCursorState: Subject<GeneralCursors> = new Subject<GeneralCursors>();
  updateGeneralCursorState(event: GeneralCursors){
    this.$generalCursorState.next(event);
  }

  getgeneralCursorState(){
    return this.$generalCursorState.asObservable();
  }

  $freeFormRoofCursorState: Subject<RoofCursors> = new Subject<RoofCursors>();
  updateFreeFormRoofCursorState(event: RoofCursors){
    this.$freeFormRoofCursorState.next(event);
  }

  getFreeFormRoofCursorState(){
    return this.$freeFormRoofCursorState.asObservable();
  }


  $panelArrayCursorState: Subject<PanelArrayCursors> = new Subject<PanelArrayCursors>();
  updatePanelArrayCursorState(event: PanelArrayCursors){
    this.$panelArrayCursorState.next(event);
  }

  getPanelArrayCursorState(){
    return this.$panelArrayCursorState.asObservable();
  }

  $multiSelectedPanels: Subject<string[]> = new Subject<string[]>();
  updateMultiSelectedPanels(event: string[]){
    this.$multiSelectedPanels.next(event);
  }

  getMultiSelectedPanels(){
    return this.$multiSelectedPanels.asObservable();
  }

  // Panel Move Cursor State
  $panelMoveCursorState: BehaviorSubject<PanelMoveCursors> = new BehaviorSubject<PanelMoveCursors>(PanelMoveCursors.NORMAL);

  updatePanelMoveCursorState(event: PanelMoveCursors) {
    this.$panelMoveCursorState.next(event);
  }

  getPanelMoveCursorState() {
    return this.$panelMoveCursorState.asObservable();
  }

  // Ruler Cursor State
  $rulerCursorState: BehaviorSubject<RulerCursors> = new BehaviorSubject<RulerCursors>(RulerCursors.NORMAL);

  updateRulerCursorState(event: RulerCursors) {
    this.$rulerCursorState.next(event);
  }

  getRulerCursorState() {
    return this.$rulerCursorState.asObservable();
  }

  // Panel Move feature
  $panelMoveSelectedPanels: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  $panelMoveConfirm: Subject<void> = new Subject<void>();
  $panelMoveCancel: Subject<void> = new Subject<void>();
  $panelMoveActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  triggerPanelMoveInit() {
    this.$panelMoveActive.next(true);
  }

  deactivatePanelMove() {
    this.$panelMoveActive.next(false);
  }

  onPanelMoveActiveChange() {
    return this.$panelMoveActive.asObservable();
  }

  isPanelMoveActive() {
    return this.$panelMoveActive.getValue();
  }

  updatePanelMoveSelectedPanels(panelIds: string[]) {
    this.$panelMoveSelectedPanels.next(panelIds);
  }

  getPanelMoveSelectedPanels() {
    return this.$panelMoveSelectedPanels.asObservable();
  }

  confirmPanelMove() {
    this.$panelMoveConfirm.next();
  }

  onPanelMoveConfirm() {
    return this.$panelMoveConfirm.asObservable();
  }

  cancelPanelMove() {
    this.$panelMoveCancel.next();
  }

  onPanelMoveCancel() {
    return this.$panelMoveCancel.asObservable();
  }

  // Panel Move Active Array tracking
  $panelMoveActiveArrayId: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  
  // Panel Move Limit Exceeded tracking
  $panelMoveLimitExceeded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  
  updatePanelMoveLimitExceeded(exceeded: boolean) {
    this.$panelMoveLimitExceeded.next(exceeded);
  }

  getPanelMoveLimitExceeded() {
    return this.$panelMoveLimitExceeded.asObservable();
  }
  
  updatePanelMoveActiveArrayId(arrayId: string | null) {
    this.$panelMoveActiveArrayId.next(arrayId);
  }

  getPanelMoveActiveArrayId() {
    return this.$panelMoveActiveArrayId.asObservable();
  }

  getCurrentPanelMoveActiveArrayId() {
    return this.$panelMoveActiveArrayId.getValue();
  }

  // Cancel Selection (clears selection but keeps mode active)
  $panelMoveCancelSelection: Subject<void> = new Subject<void>();
  
  cancelPanelMoveSelection() {
    this.$panelMoveCancelSelection.next();
  }

  onPanelMoveCancelSelection() {
    return this.$panelMoveCancelSelection.asObservable();
  }

  // Panel Move Has Moved tracking (enables confirm button)
  $panelMoveHasMoved: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  
  updatePanelMoveHasMoved(hasMoved: boolean) {
    this.$panelMoveHasMoved.next(hasMoved);
  }

  getPanelMoveHasMoved() {
    return this.$panelMoveHasMoved.asObservable();
  }

  getCurrentPanelMoveHasMoved() {
    return this.$panelMoveHasMoved.getValue();
  }

  // Panel Move Violations tracking
  $panelMoveViolations: BehaviorSubject<{ validCount: number, warningCount: number, invalidCount: number }> = new BehaviorSubject<{ validCount: number, warningCount: number, invalidCount: number }>({ validCount: 0, warningCount: 0, invalidCount: 0 });
  
  updatePanelMoveViolations(violations: { validCount: number, warningCount: number, invalidCount: number }) {
    this.$panelMoveViolations.next(violations);
  }

  getPanelMoveViolations() {
    return this.$panelMoveViolations.asObservable();
  }

  // Toast notification system for panel move/select features
  $toastNotification: Subject<ToastNotification> = new Subject<ToastNotification>();
  
  sendToastNotification(notification: ToastNotification) {
    this.$toastNotification.next(notification);
  }

  onToastNotification() {
    return this.$toastNotification.asObservable();
  }

  // Obstacle Copy Mode tracking
  $obstacleCopyCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  $obstacleCopyModeActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $obstacleCopySourceName: BehaviorSubject<string> = new BehaviorSubject<string>('');
  $obstacleCopySourceId: BehaviorSubject<string> = new BehaviorSubject<string>('');
  $exitObstacleCopyMode: Subject<void> = new Subject<void>();

  get obstacleCopyCount$(): Observable<number> {
    return this.$obstacleCopyCount.asObservable();
  }

  updateObstacleCopyCount(count: number) {
    this.$obstacleCopyCount.next(count);
  }

  incrementObstacleCopyCount() {
    this.$obstacleCopyCount.next(this.$obstacleCopyCount.getValue() + 1);
  }

  resetObstacleCopyCount() {
    this.$obstacleCopyCount.next(0);
  }

  get obstacleCopyModeActive$(): Observable<boolean> {
    return this.$obstacleCopyModeActive.asObservable();
  }

  get obstacleCopySourceName$(): Observable<string> {
    return this.$obstacleCopySourceName.asObservable();
  }

  get obstacleCopySourceId$(): Observable<string> {
    return this.$obstacleCopySourceId.asObservable();
  }

  getObstacleCopySourceId(): string {
    return this.$obstacleCopySourceId.getValue();
  }

  setObstacleCopyModeActive(active: boolean, sourceName?: string, sourceId?: string) {
    this.$obstacleCopyModeActive.next(active);
    if (active && sourceName) {
      this.$obstacleCopySourceName.next(sourceName);
    }
    if (active && sourceId) {
      this.$obstacleCopySourceId.next(sourceId);
    }
    if (!active) {
      this.resetObstacleCopyCount();
      this.$obstacleCopySourceName.next('');
      this.$obstacleCopySourceId.next('');
    }
  }

  isObstacleCopyModeActive(): boolean {
    return this.$obstacleCopyModeActive.getValue();
  }

  exitObstacleCopyMode() {
    this.$exitObstacleCopyMode.next();
  }

  onExitObstacleCopyMode(): Observable<void> {
    return this.$exitObstacleCopyMode.asObservable();
  }

  public ngOnDestroy(): void {
    if (this.frameId != null) {
      cancelAnimationFrame(this.frameId);
    }
    if (this.renderer != null) {
      this.renderer.dispose();
      this.renderer.clear();
      this.renderer = null;
      this.canvas = null;
    }
  }

  public getGoogleMap(heightDataWorker: Worker, rgbtifFlag: boolean = false) {
    const crn = this.authService.getItem('crn');
    let url = '';
    if (rgbtifFlag)
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${crn}&map_option=GOOGLEMAPS&resource_type=image&rgbtif=${rgbtifFlag}`; // to fetch  google solar image
    else
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${crn}&map_option=GOOGLEMAPS&resource_type=image`; //to fetch  google maps image
    const token = `Bearer ${localStorage.getItem('token')}`;
    heightDataWorker.postMessage({ action: 'download-google-map-image',  url, token });
  }

  public getNearMap(heightDataWorker: Worker) {
    const crn = this.authService.getItem('crn');
    const url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${crn}&map_option=NEARMAPS&resource_type=image`;
    const token = `Bearer ${localStorage.getItem('token')}`;
    heightDataWorker.postMessage({ action: 'download-near-map-image',  url, token });
  }
  // For local testing purposes
  // getheightdetails(url:any) {
  //   //todo remove before production
  //   url = 'assets/json/heightArray.json';
  //   return this.http.get(url);//Util.MapData().heightsJson);
  // }

  private _showAllDimensions = false;
  getShowAllDimensions() { return this._showAllDimensions; }
  setShowAllDimensions(value: boolean) { this._showAllDimensions = value; }

  // Tracks the user's intended ruler-label visibility. Defaults to true so
  // freshly drawn rulers show their measurement, but flips to false when the
  // D key hides all dimensions — used by `forceResyncFromStore` to restore
  // labels with the right state after undo/redo.
  private _rulerLabelsVisible = true;
  getRulerLabelsVisible() { return this._rulerLabelsVisible; }
  setRulerLabelsVisible(value: boolean) { this._rulerLabelsVisible = value; }

  public getMapHeight(heightDataWorker: Worker, hasDsmData = '', defaultMapOption = ''): void {
    const crn = this.authService.getItem('crn');
    let url = '';
    if (hasDsmData.toString() == 'false' && defaultMapOption === 'GOOGLEMAPS')
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${crn}&map_option=GOOGLEMAPS&resource_type=height`; // Height data for google maps - will be empty
    else if (hasDsmData.toString() == 'true' && defaultMapOption === 'GOOGLEMAPS')
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${crn}&map_option=GOOGLEMAPS&resource_type=height&rgbtif=${hasDsmData}`; // Height data for google Solar
    else
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${crn}&map_option=NEARMAPS&resource_type=height`;/// Height data for Near maps

      const token = `Bearer ${localStorage.getItem('token')}`;
      heightDataWorker.postMessage({ action: 'download-height-data',  url, token });
  }

}
